import React, { Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CompanyAdminAuthProvider } from './contexts/CompanyAdminAuthContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { PartnerAuthProvider } from './contexts/PartnerAuthContext';
import { AppBootScreen } from './components/AppBootScreen';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import CompanyDetailLayout from './components/CompanyDetailLayout';
import CompanyAdminLayout from './components/CompanyAdminLayout';

const lazyNamed = (loader, name) =>
  lazy(() => loader().then((mod) => ({ default: mod[name] })));

// Old Admin Panel Pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const CustomersPage = lazy(() => import('./pages/CustomersPage'));
const CustomerDetailPage = lazy(() => import('./pages/CustomerDetailPage'));

// SuperAdmin Pages
const SuperAdminLoginPage = lazy(() => import('./pages/superadmin/LoginPage'));
const SuperAdminDashboardPage = lazy(() => import('./pages/superadmin/DashboardPage'));
const SuperAdminCompaniesPage = lazy(() => import('./pages/superadmin/CompaniesPage'));
const SuperAdminDietitianRequestsPage = lazy(() => import('./pages/superadmin/DietitianRequestsPage'));
const SuperAdminCompanyUsersPage = lazy(() => import('./pages/superadmin/CompanyUsersPage'));
const SuperAdminSettingsPage = lazy(() => import('./pages/superadmin/SettingsPage'));
const SuperAdminSystemSettingsPage = lazy(() => import('./pages/superadmin/SystemSettingsPage'));
const SuperAdminAppInstallationsPage = lazy(() => import('./pages/superadmin/AppInstallationsPage'));
const SuperAdminAddonsPage = lazy(() => import('./pages/superadmin/AddonsPage'));
const SuperAdminRecipesPage = lazy(() => import('./pages/superadmin/RecipesPage'));
const SuperAdminSupportTicketsPage = lazy(() => import('./pages/superadmin/SupportTicketsPage'));
const SuperAdminVirtualAssistantsPage = lazy(() => import('./pages/superadmin/VirtualAssistantsPage'));
const SuperAdminMediaServersPage = lazy(() => import('./pages/superadmin/MediaServersPage'));
const SuperAdminPaymentGatewaysPage = lazy(() => import('./pages/superadmin/PaymentGatewaysPage'));
const SuperAdminMobilePremiumPage = lazy(() => import('./pages/superadmin/MobilePremiumPage'));
const SuperAdminDietitianPaymentsPage = lazy(() => import('./pages/superadmin/DietitianPaymentsPage'));
const SuperAdminReportsPage = lazy(() => import('./pages/superadmin/ReportsPage'));
const CompanyOverviewPage = lazy(() => import('./pages/superadmin/CompanyOverviewPage'));
const CompanyAiLimitsPage = lazy(() => import('./pages/superadmin/CompanyAiLimitsPage'));
const CompanyGatewaysPage = lazy(() => import('./pages/superadmin/CompanyGatewaysPage'));
const CompanyMediaServerPage = lazy(() => import('./pages/superadmin/CompanyMediaServerPage'));

// Company Admin Pages
const CompanyAdminLoginPage = lazy(() => import('./pages/companyAdmin/LoginPage'));
const CompanyAdminDashboardPage = lazy(() => import('./pages/companyAdmin/DashboardPage'));
const CompanyAdminCustomersPage = lazy(() => import('./pages/companyAdmin/CustomersPage'));
const CompanyAdminCustomerDetailPage = lazy(() => import('./pages/companyAdmin/CustomerDetailPage'));
const CompanyAdminUserManagementPage = lazy(() => import('./pages/companyAdmin/UserManagementPage'));
const CompanyAdminSettingsPage = lazy(() => import('./pages/companyAdmin/SettingsPage'));
const AIConfigPage = lazy(() => import('./pages/companyAdmin/AIConfigPage'));
const AIHealthPage = lazy(() => import('./pages/companyAdmin/AIHealthPage'));
const ReportsPage = lazy(() => import('./pages/companyAdmin/ReportsPage'));
const ExpiredFoodsPage = lazy(() => import('./pages/companyAdmin/ExpiredFoodsPage'));
const AIChatsPage = lazy(() => import('./pages/companyAdmin/AIChatsPage'));
const CompanyAdminChatsPage = lazy(() => import('./pages/companyAdmin/ChatsPage'));
const CompanyAdminSubscriptionsPage = lazy(() => import('./pages/companyAdmin/SubscriptionsPage'));
const CompanyAdminPaymentsPage = lazy(() => import('./pages/companyAdmin/PaymentsPage'));
const CompanyAdminAppointmentsPage = lazy(() => import('./pages/companyAdmin/AppointmentsPage'));
const PersonalAssistantPage = lazyNamed(
  () => import('./pages/companyAdmin/HireRequestPage'),
  'PersonalAssistantPage',
);
const HireDietitiansPage = lazyNamed(
  () => import('./pages/companyAdmin/HireRequestPage'),
  'HireDietitiansPage',
);
const CompanyAdminAppInstallationsPage = lazy(() => import('./pages/companyAdmin/AppInstallationsPage'));
const CompanyAdminRecipesPage = lazy(() => import('./pages/companyAdmin/RecipesPage'));
const CompanyAdminAddonsPage = lazy(() => import('./pages/companyAdmin/AddonsPage'));
const CompanyAdminBillingPage = lazy(() => import('./pages/companyAdmin/BillingPage'));
const CompanyAdminWhiteLabelingPage = lazy(() => import('./pages/companyAdmin/WhiteLabelingPage'));
const CompanyAdminTicketsPage = lazy(() => import('./pages/companyAdmin/TicketsPage'));
const CompanyAdminPartnersHubPage = lazy(() => import('./pages/companyAdmin/PartnersHubPage'));

// Partner Portal Pages
const PartnerLoginPage = lazy(() => import('./pages/partner/LoginPage'));
const PartnerDashboardPage = lazy(() => import('./pages/partner/DashboardPage'));
const PartnerCustomersPage = lazy(() => import('./pages/partner/CustomersPage'));
const PartnerPaymentHistoryPage = lazy(() => import('./pages/partner/PaymentHistoryPage'));
const PartnerWithdrawalRequestPage = lazy(() => import('./pages/partner/WithdrawalRequestPage'));
const PartnerWithdrawalHistoryPage = lazy(() => import('./pages/partner/WithdrawalHistoryPage'));
const PartnerSettingsPage = lazy(() => import('./pages/partner/SettingsPage'));

const CustomerLoginPage = lazy(() => import('./pages/customer/LoginPage'));
const CustomerChatPage = lazy(() => import('./pages/customer/ChatPage'));
const CustomerAiChatPage = lazy(() => import('./pages/customer/AiChatPage'));
const CustomerVoiceChatPage = lazy(() => import('./pages/customer/VoiceChatPage'));
const CustomerPremiumPage = lazy(() => import('./pages/customer/PremiumUpgradePage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const RegisterCreateProfilePage = lazy(() => import('./pages/RegisterCreateProfilePage'));
const RegisterSurveyPage = lazy(() => import('./pages/RegisterSurveyPage'));
const DownloadPage = lazy(() => import('./pages/DownloadPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const AddOnsPage = lazy(() => import('./pages/AddOnsPage'));
const DietPlansPage = lazy(() => import('./pages/DietPlansPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const AppPaymentReturnPage = lazy(() => import('./pages/AppPaymentReturnPage'));
const EntryPage = lazy(() => import('./pages/EntryPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const DietitianSignupPage = lazy(() => import('./pages/DietitianSignupPage'));
const DietitianVerifyPage = lazy(() => import('./pages/DietitianVerifyPage'));

// Protected Route component for SuperAdmin
const SuperAdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated || user?.user_type !== 0) {
    return <Navigate to="/superadmin/login" replace />;
  }

  return children;
};

const PLATFORM_TABS = [
  'captcha',
  'ftp',
  'urls',
  'login-auth',
  'schedulers',
  'roles',
  'events',
];

const RedirectCompanySettingsTab = () => {
  const { tab } = useParams();
  if (tab === 'mailer') {
    return <Navigate to="/superadmin/settings?tab=mailer" replace />;
  }
  const next = PLATFORM_TABS.includes(tab) ? tab : 'captcha';
  return <Navigate to={`/superadmin/platform/${next}`} replace />;
};

// Protected Route component for Admin panel
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Admin, Partner, Agent
  const isAdminUser = isAuthenticated && [1, 2, 3].includes(user?.user_type);
  if (!isAdminUser) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route (redirects to dashboard if already logged in)
const SuperAdminPublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated && user?.user_type === 0) {
    return <Navigate to="/superadmin" replace />;
  }

  return children;
};

const AdminPublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isAdminUser = isAuthenticated && [1, 2, 3].includes(user?.user_type);
  if (isAdminUser) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const CompanyAdminProtectedRoute = ({ children }) => {
  // Separate auth storage for company admin (no dependency on main AuthContext)
  const token = localStorage.getItem('company_admin_token');
  const isAuthed = !!token;

  if (!isAuthed) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Restrict certain pages for Company Admin roles
// - Agents (user_type=3) should not access Users/Groups management pages
const CompanyAdminNonAgentRoute = ({ children }) => {
  const rawUser = localStorage.getItem('company_admin_user');
  if (rawUser) {
    try {
      const u = JSON.parse(rawUser);
      if (Number(u?.user_type) === 3) {
        return <Navigate to="/admin" replace />;
      }
    } catch (e) {
      // ignore
    }
  }
  return children;
};

const CompanyAdminPublicRoute = ({ children }) => {
  const token = localStorage.getItem('company_admin_token');
  if (token) {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

// Customer protected route
const CustomerProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('customer_token');
  if (!token) {
    return <Navigate to="/customers/login" replace />;
  }
  return children;
};

// Partner Portal protected route
const PartnerProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('partner_token');
  if (!token) return <Navigate to="/partner/login" replace />;
  return children;
};

const PartnerPublicRoute = ({ children }) => {
  const token = localStorage.getItem('partner_token');
  if (token) return <Navigate to="/partner" replace />;
  return children;
};


function AppRoutes() {
  return (
    <Routes>
      {/* Old Admin Panel Routes */}
      <Route
        path="/login"
        element={
          <AdminPublicRoute>
            <LoginPage />
          </AdminPublicRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <AdminProtectedRoute>
            <DashboardPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/panel/customers"
        element={
          <AdminProtectedRoute>
            <CustomersPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="/panel/customers/:id"
        element={
          <AdminProtectedRoute>
            <CustomerDetailPage />
          </AdminProtectedRoute>
        }
      />

      {/* Company Admin Routes — one layout so sidebar/query cache survive tab changes */}
      <Route path="/admin">
        <Route
          path="login"
          element={
            <CompanyAdminPublicRoute>
              <CompanyAdminLoginPage />
            </CompanyAdminPublicRoute>
          }
        />
        <Route
          element={
            <CompanyAdminProtectedRoute>
              <CompanyAdminLayout />
            </CompanyAdminProtectedRoute>
          }
        >
          <Route index element={<CompanyAdminDashboardPage />} />
          <Route path="chats" element={<CompanyAdminChatsPage />} />
          <Route path="customers" element={<CompanyAdminCustomersPage />} />
          <Route path="customers/:id" element={<CompanyAdminCustomerDetailPage />} />
          <Route path="app-installations" element={<CompanyAdminAppInstallationsPage />} />
          <Route path="addons" element={<CompanyAdminAddonsPage />} />
          <Route path="billing" element={<CompanyAdminBillingPage />} />
          <Route path="white-labeling" element={<CompanyAdminWhiteLabelingPage />} />
          <Route path="recipes" element={<CompanyAdminRecipesPage />} />
          <Route
            path="user-management"
            element={
              <CompanyAdminNonAgentRoute>
                <CompanyAdminUserManagementPage />
              </CompanyAdminNonAgentRoute>
            }
          />
          <Route
            path="users"
            element={
              <CompanyAdminNonAgentRoute>
                <Navigate to="/admin/user-management?tab=users" replace />
              </CompanyAdminNonAgentRoute>
            }
          />
          <Route path="subscriptions" element={<CompanyAdminSubscriptionsPage />} />
          <Route path="payments" element={<CompanyAdminPaymentsPage />} />
          <Route path="payment-history" element={<Navigate to="/admin/payments?tab=history" replace />} />
          <Route path="payments/gateways" element={<Navigate to="/admin/payments?tab=gateways" replace />} />
          <Route path="payments/plans" element={<Navigate to="/admin/payments?tab=plans" replace />} />
          <Route path="appointments" element={<CompanyAdminAppointmentsPage />} />
          <Route path="personal-assistant" element={<PersonalAssistantPage />} />
          <Route path="hire-dietitians" element={<HireDietitiansPage />} />
          <Route
            path="groups"
            element={
              <CompanyAdminNonAgentRoute>
                <Navigate to="/admin/user-management?tab=groups" replace />
              </CompanyAdminNonAgentRoute>
            }
          />
          <Route path="settings" element={<CompanyAdminSettingsPage />} />
          <Route path="ai-config" element={<AIConfigPage />} />
          <Route path="ai-health" element={<AIHealthPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="expired-foods" element={<ExpiredFoodsPage />} />
          <Route path="ai-chats" element={<AIChatsPage />} />
          <Route path="support" element={<CompanyAdminTicketsPage mode="support" />} />
          <Route path="tickets" element={<CompanyAdminTicketsPage />} />
          <Route
            path="partners"
            element={
              <CompanyAdminNonAgentRoute>
                <CompanyAdminPartnersHubPage />
              </CompanyAdminNonAgentRoute>
            }
          />
          <Route
            path="partners/withdrawals"
            element={
              <CompanyAdminNonAgentRoute>
                <Navigate to="/admin/partners?tab=withdrawals" replace />
              </CompanyAdminNonAgentRoute>
            }
          />
        </Route>
      </Route>


      {/* Customer Routes */}
      <Route path="/customers" element={<Navigate to="/customers/login" replace />} />
      <Route path="/customers/login" element={<CustomerLoginPage />} />
      <Route
        path="/customers/chat"
        element={
          <CustomerProtectedRoute>
            <CustomerChatPage />
          </CustomerProtectedRoute>
        }
      />
      <Route
        path="/customers/ai-chat"
        element={
          <CustomerProtectedRoute>
            <CustomerAiChatPage />
          </CustomerProtectedRoute>
        }
      />
      <Route
        path="/customers/ai-voice-chat"
        element={
          <CustomerProtectedRoute>
            <CustomerVoiceChatPage />
          </CustomerProtectedRoute>
        }
      />
      <Route
        path="/customers/premium"
        element={
          <CustomerProtectedRoute>
            <CustomerPremiumPage />
          </CustomerProtectedRoute>
        }
      />

      {/* Public Registration & Download */}
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/register/wizard" element={<RegisterCreateProfilePage />} />
      <Route path="/register/survey" element={<RegisterSurveyPage />} />
      <Route path="/download" element={<DownloadPage />} />
      <Route path="/dietitian/signup" element={<DietitianSignupPage />} />
      <Route path="/dietitian/verify" element={<DietitianVerifyPage />} />

      {/* SuperAdmin Routes */}
      <Route
        path="/superadmin/login"
        element={
          <SuperAdminPublicRoute>
            <SuperAdminLoginPage />
          </SuperAdminPublicRoute>
        }
      />
      <Route
        path="/superadmin"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminDashboardPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/companies"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminCompaniesPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/dietitian-requests"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminDietitianRequestsPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/companies/:company_id/users"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminCompanyUsersPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/companies/:company_id/app-installations"
        element={
          <SuperAdminProtectedRoute>
            <Navigate to="/superadmin/app-installations" replace />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/companies/:company_id/settings/:tab"
        element={
          <SuperAdminProtectedRoute>
            <RedirectCompanySettingsTab />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/companies/:company_id/settings"
        element={
          <SuperAdminProtectedRoute>
            <Navigate to="/superadmin/settings" replace />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/companies/:company_id"
        element={
          <SuperAdminProtectedRoute>
            <CompanyDetailLayout />
          </SuperAdminProtectedRoute>
        }
      >
        <Route index element={<CompanyOverviewPage />} />
        <Route path="ai-limits" element={<CompanyAiLimitsPage />} />
        <Route path="ai-chats" element={<AIChatsPage />} />
        <Route path="ai-config" element={<AIConfigPage />} />
        <Route path="ai-health" element={<AIHealthPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="gateways" element={<CompanyGatewaysPage />} />
        <Route path="media-server" element={<CompanyMediaServerPage />} />
      </Route>
      <Route
        path="/superadmin/addons"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminAddonsPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/tickets"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminSupportTicketsPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/virtual-assistants"
        element={
          <SuperAdminProtectedRoute>
            <Navigate to="/superadmin/virtual-assistants/personal-assistant" replace />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/virtual-assistants/personal-assistant"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminVirtualAssistantsPage type="personal_assistant" />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/virtual-assistants/personal-dietitian"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminVirtualAssistantsPage type="personal_dietitian" />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/support"
        element={<Navigate to="/superadmin/tickets" replace />}
      />
      <Route
        path="/superadmin/sales"
        element={<Navigate to="/superadmin/tickets?category=sales" replace />}
      />
      <Route
        path="/superadmin/recipes"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminRecipesPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/media-servers"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminMediaServersPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/payment-methods"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminPaymentGatewaysPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/mobile-premium"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminMobilePremiumPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/dietitian-payments"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminDietitianPaymentsPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/reports"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminReportsPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/app-installations"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminAppInstallationsPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/settings"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminSettingsPage />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/platform/mailer"
        element={
          <SuperAdminProtectedRoute>
            <Navigate to="/superadmin/settings?tab=mailer" replace />
          </SuperAdminProtectedRoute>
        }
      />
      <Route
        path="/superadmin/platform/:tab"
        element={
          <SuperAdminProtectedRoute>
            <SuperAdminSystemSettingsPage />
          </SuperAdminProtectedRoute>
        }
      />

      {/* Partner Portal Routes */}
      <Route path="/partner/login" element={<PartnerPublicRoute><PartnerLoginPage /></PartnerPublicRoute>} />
      <Route path="/partner" element={<PartnerProtectedRoute><PartnerDashboardPage /></PartnerProtectedRoute>} />
      <Route path="/partner/customers" element={<PartnerProtectedRoute><PartnerCustomersPage /></PartnerProtectedRoute>} />
      <Route path="/partner/payment-history" element={<PartnerProtectedRoute><PartnerPaymentHistoryPage /></PartnerProtectedRoute>} />
      <Route path="/partner/withdrawals/request" element={<PartnerProtectedRoute><PartnerWithdrawalRequestPage /></PartnerProtectedRoute>} />
      <Route path="/partner/withdrawals/history" element={<PartnerProtectedRoute><PartnerWithdrawalHistoryPage /></PartnerProtectedRoute>} />
      <Route path="/partner/settings" element={<PartnerProtectedRoute><PartnerSettingsPage /></PartnerProtectedRoute>} />

      {/* Mobile App Links / Universal Links return */}
      <Route path="/app/payment" element={<AppPaymentReturnPage />} />

      {/* Public marketing site */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/add-ons" element={<AddOnsPage />} />
      <Route path="/diet-plans" element={<DietPlansPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/portals" element={<EntryPage />} />

      {/* Catch all - no auto redirect to superadmin */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 120_000,
            gcTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
  );

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      storageKey="wingsdiet-theme"
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <CompanyAdminAuthProvider>
            <CustomerAuthProvider>
              <PartnerAuthProvider>
                <BrandingProvider>
                  <Router>
                    <RouteErrorBoundary>
                      <Suspense fallback={<AppBootScreen />}>
                        <AppRoutes />
                      </Suspense>
                    </RouteErrorBoundary>
                    <Toaster
                      position="top-right"
                      richColors
                      toastOptions={{
                        style: {
                          fontFamily: 'Nunito, sans-serif',
                        },
                      }}
                    />
                  </Router>
                </BrandingProvider>
              </PartnerAuthProvider>
            </CustomerAuthProvider>
          </CompanyAdminAuthProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
